import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { conditionsConfig, filterConfig, RulesFilterEntity } from '@pdqdev/pdq-shared/dist/entities';
import { IBoxSettingsData } from '@pdqdev/pdq-shared/dist/interfaces';
import { PackageOptions } from './package.model';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BoxService } from '@app/services/boxes/box.service';
import { MatDialog } from '@angular/material/dialog';
import { DeletePackageComponent } from './delete-package/delete-package.component';
import { ShopService } from '@app/services/shops/shop.service';
import { PaginationCursorAction } from '@app/storybook/layouts/components/footer/pagination/pagination.component';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

const PACKAGES_PAGE_SIZE = 50;

@Component({
  selector: 'app-settings-packages',
  templateUrl: './packages.component.html',
  styleUrls: ['./packages.component.scss'],
  standalone: false,
})
export class SettingsPackagesComponent implements OnInit {
  packages: IBoxSettingsData[];
  filteredPackages: IBoxSettingsData[] = [];
  pagedPackages: IBoxSettingsData[] = [];
  searchTerm = '';
  pageIndex = 0;
  rangeLabel = '';
  hasNextPage = false;
  hasPreviousPage = false;
  packagesForm: UntypedFormGroup;
  options;
  pdqIqSettingsOptions;
  isPdqIqEnabled = false;
  isPdqIqSettingsConfigured = false;
  isPaccurateEnabled = false;
  notification = { msg: '', type: 'info', mode: 'top', state: 'out' };
  rulesFilterEntity = RulesFilterEntity;
  readonly searchControl = new FormControl('', { nonNullable: true });

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private boxService: BoxService,
    private dialog: MatDialog,
    private shopService: ShopService,
  ) {}

  ngOnInit(): void {
    this.shopService.refreshShop();

    this.options = [
      { label: 'Edit', value: PackageOptions.edit },
      { label: 'Duplicate', value: PackageOptions.duplicate },
      { label: 'Delete', value: PackageOptions.delete },
    ];

    this.pdqIqSettingsOptions = [{ label: 'Settings', value: 'pdqIqSettings' }];
    this.getPdqIqSettings();

    const formFields: any = {};
    this.shopService.shop$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((shop) => {
      this.isPaccurateEnabled = shop?.metadata?.paccurate?.enabled;
      formFields.isPaccurateEnabled = this.isPaccurateEnabled;
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => this.onSearch(term));

    this.boxService.getBoxes({ excludeCustomBoxes: true }).subscribe((data) => {
      this.packages = data.sort((a, b) => a.priority - b.priority);
      this.packages.forEach((data) => {
        data.rules?.conditionsSubsets?.forEach((rules) => {
          rules.rulesArray.forEach((rule) => {
            const filterConfigItem = filterConfig.find((x) => x.value === rule.filterEntity);
            rule['displayEntity'] = filterConfigItem?.name;
            rule['displayCondition'] = conditionsConfig
              .filter((x) => x.conditionId === filterConfigItem?.conditionId)
              .find((x) => x.value === rule.conditionEntity)?.name;
          });
        });
      });
      if (this.packages?.length) {
        this.packages?.forEach((p) => {
          formFields[p.id] = [p.isActive];
        });
      }
      this.packagesForm = this.formBuilder.group(formFields);
      this.updatePage();
    });
  }

  packageActiveChanged(pack: IBoxSettingsData) {
    pack.isActive = !pack.isActive;
    this.boxService.updateBox(pack).subscribe({
      next: () => this.shopService.refreshBoxes(),
      error: (error) => {
        if (error.status === 403) {
          pack.isActive = !pack.isActive;
          this.packagesForm.get(pack.id.toString()).setValue(pack.isActive);
        }
      },
    });
  }

  onChange(item, box: IBoxSettingsData) {
    switch (item.value) {
      case PackageOptions.delete: {
        const dialogRef = this.dialog.open(DeletePackageComponent, {
          width: '612px',
          height: '232px',
          panelClass: 'padding-0',
          data: {
            packageName: box.name,
          },
        });
        dialogRef.afterClosed().subscribe((isDelete) => {
          if (isDelete) {
            this.boxService.deletePackage(box.id).subscribe(() => {
              this.showNotification('Package deleted');
              this.shopService.refreshBoxes();
              this.packages = this.packages.filter((p) => p.id !== box.id);
              this.updatePage();
            });
          }
        });
        break;
      }
      case PackageOptions.duplicate: {
        this.router.navigate([`./edit-package/duplicate-${box.id}`], { relativeTo: this.route });
        break;
      }
      case PackageOptions.edit: {
        this.router.navigate([`./edit-package/${box.id}`], { relativeTo: this.route });
        break;
      }
    }
  }

  onPdqIqSettingsChange(item) {
    if (item.value === 'pdqIqSettings') {
      this.router.navigate(['./pdq-iq'], { relativeTo: this.route });
    }
  }

  navigateToAddPackage() {
    this.router.navigate(['./edit-package'], { relativeTo: this.route });
  }

  onCursorPageChange(action: PaginationCursorAction) {
    const lastPageIndex = Math.ceil(this.filteredPackages.length / PACKAGES_PAGE_SIZE) - 1;
    const pageIndexByAction: Record<PaginationCursorAction, number> = {
      first: 0,
      prev: this.pageIndex - 1,
      next: this.pageIndex + 1,
      last: lastPageIndex,
      current: this.pageIndex,
    };
    this.pageIndex = pageIndexByAction[action];
    this.updatePage();
  }

  // Drag is disabled while searching, so the page is a contiguous slice of `packages` and only needs the page offset
  drop(event: CdkDragDrop<IBoxSettingsData[]>) {
    const pageOffset = this.pageIndex * PACKAGES_PAGE_SIZE;
    moveItemInArray(this.packages, pageOffset + event.previousIndex, pageOffset + event.currentIndex);
    this.updatePage();
    const boxesPriorities = this.packages.map((box, index) => ({ id: box.id, priority: index + 1 }));
    this.boxService.changePriorityToBox(boxesPriorities).subscribe(() => this.shopService.refreshBoxes());
  }

  showNotification(msg: string, type?: 'success' | 'failure' | 'info') {
    this.notification.msg = msg;
    this.notification.state = 'in';
    if (type) {
      this.notification.type = type;
    }
  }

  activatePdqIq() {
    const isActive = this.packagesForm.get('isPaccurateEnabled').value;
    this.boxService.updatePaccurateState(isActive).subscribe(() => this.shopService.refreshShop());
  }

  getPdqIqSettings() {
    this.boxService.getPdqIqSettings().subscribe((settings) => {
      this.isPdqIqEnabled = settings?.enabled;
      this.isPdqIqSettingsConfigured = settings?.isReadyForPaccurate;
    });
  }

  private onSearch(term: string) {
    this.searchTerm = term.trim().toLowerCase();
    this.pageIndex = 0;
    this.updatePage();
  }

  private updatePage() {
    this.filteredPackages = this.searchTerm
      ? this.packages.filter((p) => p.name?.toLowerCase().includes(this.searchTerm))
      : this.packages;
    const totalPages = Math.max(1, Math.ceil(this.filteredPackages.length / PACKAGES_PAGE_SIZE));
    this.pageIndex = Math.min(Math.max(this.pageIndex, 0), totalPages - 1);
    const start = this.pageIndex * PACKAGES_PAGE_SIZE;
    this.pagedPackages = this.filteredPackages.slice(start, start + PACKAGES_PAGE_SIZE);
    this.rangeLabel = `${start + 1}-${start + this.pagedPackages.length} of ${this.filteredPackages.length}`;
    this.hasPreviousPage = this.pageIndex > 0;
    this.hasNextPage = this.pageIndex < totalPages - 1;
  }
}
